import { BoloBanner } from './Bolo.tsx';
import { AddressFlagsBanner } from './People.tsx';
import { useState } from 'react';
import { api, fileToBase64 } from '../shared/api.ts';
import { Link, useRouter } from '../shared/router.tsx';
import { Card, ErrorBox, Field, Notice, Spinner, useAction, useLoad } from '../shared/ui.tsx';

interface Defendant {
  name: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  lat: string;
  lng: string;
  vehicle: string;
  employer: string;
  employerAddress: string;
  phone: string;
}
const blank = (): Defendant => ({ name: '', line1: '', line2: '', city: '', state: 'SC', zip: '', lat: '', lng: '', vehicle: '', employer: '', employerAddress: '', phone: '' });

/** INT-001 / INT-003 / INT-007: manual office intake, one paper per defendant, duplicate warning. */
export function Intake() {
  const meta = useLoad(() => api.get('/api/meta'));
  const { navigate } = useRouter();
  const [f, setF] = useState({ paperTypeCode: '', caseNumber: '', plaintiff: '', attorney: '', courtId: '', customerId: '', courtDate: '', courtTime: '', issueDate: '', specialInstructions: '', feeCents: '0' });
  const [ivD, setIvD] = useState(false);
  const [defs, setDefs] = useState<Defendant[]>([blank()]);
  const [file, setFile] = useState<File | null>(null);
  const [created, setCreated] = useState<{ id: string; civilosNumber: string; defendant: string }[] | null>(null);
  const [boloHits, setBoloHits] = useState<any[]>([]);
  const [addrFlags, setAddrFlags] = useState<Record<string, any[]>>({});
  const a = useAction();
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  if (meta.loading) return <Spinner />;
  const m = meta.data!;
  const dupDetails = (a.error as any)?.details?.code === 'DUPLICATE_WARNING';

  const submit = (overrideDuplicates = false) =>
    a.run(async () => {
      const r = await api.post('/api/papers', {
        ...f,
        attorney: f.attorney || undefined,
        courtId: f.courtId || undefined,
        customerId: f.customerId || undefined,
        courtDate: f.courtDate || undefined,
        courtTime: f.courtTime || undefined,
        issueDate: f.issueDate || undefined,
        feeCents: f.feeCents === '' ? undefined : Math.round(Number(f.feeCents) * 100),
        ivD,
        overrideDuplicates,
        defendants: defs.map((d) => ({
          name: d.name,
          vehicle: d.vehicle || undefined,
          phone: d.phone || undefined,
          employer: d.employer || undefined,
          employerAddress: d.employerAddress || undefined,
          address: { line1: d.line1, line2: d.line2 || undefined, city: d.city || undefined, state: d.state || undefined, zip: d.zip || undefined, lat: d.lat ? Number(d.lat) : undefined, lng: d.lng ? Number(d.lng) : undefined },
        })),
      });
      if (file) {
        const dataBase64 = await fileToBase64(file);
        for (const p of r.papers) await api.post(`/api/papers/${p.id}/documents`, { filename: file.name, dataBase64, kind: 'intake_original' });
      }
      setBoloHits(r.bolos ?? []);
      setAddrFlags(r.addressFlags ?? {});
      setCreated(r.papers);
    });

  if (created) {
    return (
      <div className="stack">
        <h1>Intake saved</h1>
        <BoloBanner hits={boloHits} />
        {created.map((c) => (addrFlags[c.id] ? <AddressFlagsBanner key={c.id} flags={addrFlags[c.id]!} title={`Address history — ${c.civilosNumber} ${c.defendant}`} /> : null))}
        <Notice tone="ok">
          Created {created.length} paper{created.length > 1 ? 's' : ''}. Each needs clerk review before it can be assigned.
        </Notice>
        <Card>
          <ul className="plain">
            {created.map((p) => (
              <li key={p.id} className="row gap between">
                <span>
                  <strong className="mono">{p.civilosNumber}</strong> — {p.defendant}
                </span>
                <span className="row gap">
                  <Link to={`/papers/${p.id}/label`} className="btn sm">
                    Print label
                  </Link>
                  <Link to={`/papers/${p.id}`} className="btn sm primary">
                    Open
                  </Link>
                </span>
              </li>
            ))}
          </ul>
        </Card>
        <button className="btn" onClick={() => (setCreated(null), setDefs([blank()]), setFile(null))}>
          Start another intake
        </button>
      </div>
    );
  }

  const unsaved = !!(f.caseNumber || f.plaintiff || f.paperTypeCode || defs.some((d) => d.name || d.line1));
  return (
    <form
      data-unsaved={unsaved ? 'true' : undefined}
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        submit(false);
      }}
    >
      <h1>New intake</h1>
      <Card title="Case">
        <div className="grid2">
          <Field label="Paper type *">
            <select value={f.paperTypeCode} onChange={set('paperTypeCode')} required>
              <option value="">Select…</option>
              {m.paperTypes.map((t: any) => (
                <option key={t.code} value={t.code}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Case number *">
            <input value={f.caseNumber} onChange={set('caseNumber')} required />
          </Field>
          <Field label="Court">
            <select value={f.courtId} onChange={set('courtId')}>
              <option value="">—</option>
              {m.courts.map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Customer / submitting party">
            <select value={f.customerId} onChange={set('customerId')}>
              <option value="">—</option>
              {m.customers.map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.customer_class})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Plaintiff / petitioner">
            <input value={f.plaintiff} onChange={set('plaintiff')} />
          </Field>
          <Field label="Attorney for plaintiff" hint="Goes in the Writ Book">
            <input value={f.attorney} onChange={set('attorney')} />
          </Field>
          <Field label="Issue date">
            <input type="date" value={f.issueDate} onChange={set('issueDate')} />
          </Field>
          <Field label="Court / return date">
            <input type="date" value={f.courtDate} onChange={set('courtDate')} />
          </Field>
          <Field label="Court time">
            <input type="time" value={f.courtTime} onChange={set('courtTime')} />
          </Field>
          <Field label="Fee ($)" hint="Starts at $0 (no fee). Enter the fee if this paper has one, or clear the box to use the agency fee schedule.">
            <input type="number" min="0" step="0.01" value={f.feeCents} onChange={set('feeCents')} />
          </Field>
          <Field label="Source document (PDF/image)" hint="Stored as the immutable intake original.">
            <input type="file" accept="application/pdf,image/png,image/jpeg,image/tiff" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </Field>
        </div>
        <Field label="Special instructions">
          <textarea rows={2} value={f.specialInstructions} onChange={set('specialInstructions')} />
        </Field>
        <label className="row gap-sm ivd-check">
          <input type="checkbox" checked={ivD} onChange={(e) => setIvD(e.target.checked)} /> IV-D (child support) paper
        </label>
      </Card>

      {defs.map((d, i) => {
        const up = (k: keyof Defendant) => (e: { target: { value: string } }) => setDefs(defs.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)));
        return (
          <Card
            key={i}
            title={`Defendant ${i + 1}`}
            actions={defs.length > 1 && <button type="button" className="btn sm ghost" onClick={() => setDefs(defs.filter((_, j) => j !== i))}>Remove</button>}
          >
            <div className="grid2">
              <Field label="Person to serve *">
                <input value={d.name} onChange={up('name')} required />
              </Field>
              <Field label="Street address *">
                <input value={d.line1} onChange={up('line1')} required />
              </Field>
              <Field label="Unit / line 2">
                <input value={d.line2} onChange={up('line2')} />
              </Field>
              <Field label="City">
                <input value={d.city} onChange={up('city')} />
              </Field>
              <div className="row gap">
                <Field label="State">
                  <input value={d.state} onChange={up('state')} maxLength={2} style={{ width: 70 }} />
                </Field>
                <Field label="ZIP">
                  <input value={d.zip} onChange={up('zip')} style={{ width: 110 }} />
                </Field>
              </div>
              <div className="row gap">
                <Field label="Latitude" hint="Optional map pin">
                  <input value={d.lat} onChange={up('lat')} inputMode="decimal" />
                </Field>
                <Field label="Longitude">
                  <input value={d.lng} onChange={up('lng')} inputMode="decimal" />
                </Field>
              </div>
              <Field label="Vehicle" hint="Make, color, plate — printed on the cover sheet">
                <input value={d.vehicle} onChange={up('vehicle')} />
              </Field>
              <Field label="Employer">
                <input value={d.employer} onChange={up('employer')} />
              </Field>
              <Field label="Employer address / hours">
                <input value={d.employerAddress} onChange={up('employerAddress')} />
              </Field>
              <Field label="Phone">
                <input value={d.phone} onChange={up('phone')} inputMode="tel" />
              </Field>
            </div>
          </Card>
        );
      })}
      <div className="row gap">
        <button type="button" className="btn" onClick={() => setDefs([...defs, blank()])}>
          + Add defendant (same case)
        </button>
        <span className="muted small">Each defendant becomes a separate service paper linked to one case.</span>
      </div>
      <ErrorBox error={a.error} />
      {dupDetails && (
        <Notice tone="warn">
          These look like duplicates of open papers. Review them, then{' '}
          <button type="button" className="btn sm warn" onClick={() => submit(true)} disabled={a.busy}>
            Create anyway (override)
          </button>
        </Notice>
      )}
      <div className="row gap">
        <button className="btn primary" disabled={a.busy}>
          {a.busy ? 'Saving…' : 'Create paper(s)'}
        </button>
        <button type="button" className="btn ghost" onClick={() => navigate('/papers')}>
          Cancel
        </button>
      </div>
    </form>
  );
}
